import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  useRemoveMcpServerMutation,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import type {
  IntegrationProvider,
  IntegrationSettings,
} from '@littlebird/integrations';

const REMOVE_NOTE_BY_TEMPLATE: Readonly<Record<string, string>> = {
  'apple-mail':
    'The search index stays on this Mac, so adding Apple Mail again is quick.',
};

type LocalMcpSetting = Extract<IntegrationSettings, { kind: 'local_mcp' }>;

function LocalMcpRemoveAction({
  provider,
  setting,
}: {
  provider: IntegrationProvider;
  setting: LocalMcpSetting;
}) {
  const [confirming, setConfirming] = useState(false);
  const remove = useRemoveMcpServerMutation();
  const note = setting.trustedTemplateId
    ? REMOVE_NOTE_BY_TEMPLATE[setting.trustedTemplateId]
    : undefined;

  return (
    <YStack className="shrink-0 items-end">
      <button
        type="button"
        title="Remove"
        aria-label={`Remove ${provider.name}`}
        disabled={remove.isPending}
        onClick={() => setConfirming(true)}
        className="rounded-md p-1.5 text-foreground-muted transition-colors hover:bg-secondary hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Icons.trash className="size-4" />
      </button>
      <Dialog.Root open={confirming} onOpenChange={setConfirming}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header className="space-y-3">
            <Dialog.Title>Remove {provider.name}?</Dialog.Title>
            <Dialog.Description>
              This removes {provider.name} from Littlebird. You&apos;ll need to
              add it again to use it.
              {note && ` ${note}`}
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setConfirming(false);
                remove.mutate(setting.serverSlug);
              }}
            >
              Remove {provider.name}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </YStack>
  );
}

// Tailored tabs have no Enabled switch, so a server turned off in the old
// editor needs a way back on
function LocalMcpResumeAction({
  provider,
  setting,
}: {
  provider: IntegrationProvider;
  setting: LocalMcpSetting;
}) {
  const update = useUpdateMcpServerMutation();
  return (
    <button
      type="button"
      title="Resume"
      aria-label={`Resume ${provider.name}`}
      disabled={update.isPending}
      onClick={() =>
        update.mutate({ slug: setting.serverSlug, changes: { enabled: true } })
      }
      className="rounded-md p-1.5 text-foreground-muted transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icons.playAlt className="size-4" />
    </button>
  );
}

export function ProviderHeaderAction({
  provider,
}: {
  provider: IntegrationProvider;
}) {
  const localMcpSetting = provider.settings.find(
    (setting): setting is LocalMcpSetting => setting.kind === 'local_mcp'
  );
  if (!localMcpSetting) return null;
  const disabled = provider.localInstallations.some(
    (installation) =>
      installation.id === localMcpSetting.installationId &&
      !installation.enabled
  );
  return (
    <XStack className="shrink-0 items-start gap-1">
      {disabled && (
        <LocalMcpResumeAction provider={provider} setting={localMcpSetting} />
      )}
      <LocalMcpRemoveAction provider={provider} setting={localMcpSetting} />
    </XStack>
  );
}
